import React from 'react';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import Radio from '@material-ui/core/Radio';
import FormControl from '@material-ui/core/FormControl';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Select from 'react-select';

import { FormStyled, Row, Column } from '../../../css/styles/form';
import { SelectStyled } from '../../../css/styles/select';

const MONEY_INPUT_REGEX = /^(\d{1,3}(\.\d{3})*|\d+),(\d{2})$/;

const formatIntegerPart = integerPart => {
  const digitsOnly = integerPart.replace(/\D/g, '');

  if (!digitsOnly) {
    return '0';
  }

  return digitsOnly.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const sanitizeMoneyInput = inputValue => {
  if (inputValue === null || inputValue === undefined) {
    return '';
  }

  const digitsOnly = inputValue
    .toString()
    .replace(/\D/g, '')
    .replace(/^0+(?=\d)/, '');

  if (!digitsOnly) {
    return '';
  }

  const normalizedDigits = digitsOnly || '0';
  const centsValue = normalizedDigits.padStart(3, '0');
  const integerPart = centsValue.slice(0, -2) || '0';
  const decimalPart = centsValue.slice(-2);

  return `${formatIntegerPart(integerPart)},${decimalPart}`;
};

const isValidMoneyInput = value => !value || MONEY_INPUT_REGEX.test(value);

const formatMoneyForInput = value => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const valueString = value.toString().trim();

  if (!valueString) {
    return '';
  }

  if (MONEY_INPUT_REGEX.test(valueString)) {
    return valueString;
  }

  if (/^\d+(\.\d+)?$/.test(valueString)) {
    return Number(valueString).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return valueString;
};

const handleValidate = props => ({
  due_date,

  account,

  value,

  paid_value,

  payment_date,
}) => {
  const errors = {};

  if (!due_date) errors.due_date = 'OBRIGATÓRIO';

  if (!value) errors.value = 'OBRIGATÓRIO';

  if (!props.installment && value && !isValidMoneyInput(value)) {
    errors.value = 'INFORME APENAS NÚMEROS VÁLIDOS';
  }

  if (!props.installment && paid_value && !isValidMoneyInput(paid_value)) {
    errors.paid_value = 'INFORME APENAS NÚMEROS VÁLIDOS';
  }

  if (!payment_date) errors.payment_date = 'OBRIGATÓRIO';

  if (payment_date && !account) errors.account = 'OBRIGATÓRIO';

  if (!payment_date && account) errors.payment_date = 'OBRIGATÓRIO';

  if (!payment_date && paid_value) errors.payment_date = 'OBRIGATÓRIO';

  return errors;
};

const handleSubmit = props => async (values, { setSubmitting, resetForm }) => {
  if (props.installment && props.installment.uuid) {
    await props.onUpdate({ ...values });
  } else {
    await props.onCreation({ ...values });
  }
  setSubmitting(false);
  resetForm();
};

const InstallmentForm = props => {
  const { accounts, installment, onBaixa, pay_off, onNew } = props;

  const isNewInstallment = !(installment && installment.uuid);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        due_date: installment ? installment.due_date.substring(0, 10) : '',

        payment_date:
          installment && installment.payment_date
            ? installment.payment_date.substring(0, 10)
            : '',

        value: formatMoneyForInput(installment ? installment.value : ''),

        paid_value: formatMoneyForInput(
          installment ? installment.paid_value : ''
        ),

        pay_off: pay_off || false,

        account: (installment && installment.account) || null,
      }}
      validate={handleValidate(props)}
      onSubmit={handleSubmit(props)}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleReset,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        dirty,
      }) => {
        const handleMoneyChange = event => {
          const { name, value } = event.target;

          setFieldValue(name, sanitizeMoneyInput(value));
        };

        return (
        <FormStyled>
          <h1>PARCELAS</h1>

          <ul>
            <Row>
              <Column>
                <strong>VENCIMENTO</strong>
                <input
                  id="due_date"
                  name="due_date"
                  type="date"
                  value={values.due_date}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                {errors.due_date && touched.due_date && (
                  <span>{errors.due_date}</span>
                )}
              </Column>

              <Column>
                <strong>DATA DE PAGAMENTO</strong>
                <input
                  id="payment_date"
                  name="payment_date"
                  type="date"
                  value={values.payment_date}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />

                {errors.payment_date && touched.payment_date && (
                  <span>{errors.payment_date}</span>
                )}
              </Column>
            </Row>
            <Row>
              <Column>
                <strong>VALOR</strong>
                <input
                  id="value"
                  name="value"
                  type="text"
                  value={values.value}
                  inputMode="numeric"
                  pattern="[0-9.,]*"
                  onChange={handleMoneyChange}
                  onBlur={handleBlur}
                />

                {errors.value && touched.value && <span>{errors.value}</span>}
              </Column>

              <Column>
                <strong>VALOR PAGO</strong>
                <input
                  id="paid_value"
                  name="paid_value"
                  type="text"
                  value={values.paid_value}
                  inputMode="numeric"
                  pattern="[0-9.,]*"
                  onChange={handleMoneyChange}
                  onBlur={handleBlur}
                />

                {errors.paid_value && touched.paid_value && (
                  <span>{errors.paid_value}</span>
                )}
              </Column>
            </Row>

            <Row>
              <Column>
                <FormControl style={{ width: '90%' }}>
                  <label
                    htmlFor="pay_off"
                    style={{
                      fontSize: 14,
                      fontWeight: 'bold',
                      textAlign: 'left',
                      marginTop: 16,
                    }}
                  >
                    QUITAÇÃO
                  </label>
                  <RadioGroup
                    id="pay_off"
                    aria-label="pay_off"
                    name="pay_off"
                    value={values.pay_off}
                    onChange={handleChange}
                    row
                    InputLabelProps={{ shrink: true }}
                  >
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="Não"
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="Sim"
                      labelPlacement="end"
                    />
                  </RadioGroup>
                </FormControl>
              </Column>
            </Row>

            <Row>
              <Column>
                <strong>CONTA</strong>
                <Select
                  styles={SelectStyled}
                  id="account"
                  value={values.account}
                  placeholder="Selecione uma conta"
                  onChange={selectedOption => {
                    setFieldValue('account', selectedOption);
                  }}
                  isSearchable
                  options={accounts}
                  getOptionLabel={item =>
                    `${item.name} (${
                      item.balance
                        ? item.balance.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                          })
                        : '0,00'
                    }) `
                  }
                  getOptionValue={item => item.uuid}
                  name="account"
                  isLoading={false}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  loadingMessage={() => 'Buscando contas'}
                  noOptionsMessage={() => 'Não há contas cadastradas'}
                />

                {errors.account && touched.account && (
                  <span>{errors.account}</span>
                )}
              </Column>
            </Row>

            <Row>
              <div className="buttons-container">
                {!installment ||
                  (!installment.payment_date && (
                    <>
                      <button type="submit" disabled={isSubmitting || !dirty}>
                        SALVAR
                      </button>

                      <button
                        type="button"
                        onClick={handleReset}
                        disabled={!dirty || isSubmitting}
                      >
                        RESET
                      </button>
                    </>
                  ))}

                {installment && installment.payment_date && (
                  <button className="delete" type="button" onClick={onBaixa}>
                    CANCELAR BAIXA
                  </button>
                )}

                {installment && (
                  <button
                    type="button"
                    onClick={onNew}
                    disabled={(!dirty && !installment) || isSubmitting}
                  >
                    LIMPAR
                  </button>
                )}
              </div>
            </Row>
          </ul>
        </FormStyled>
        );
      }}
    </Formik>
  );
};

InstallmentForm.propTypes = {
  accounts: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
      balance: PropTypes.number,
    })
  ),
  installment: PropTypes.shape({
    payment_date: PropTypes.string,
    paid_value: PropTypes.string,
    account: PropTypes.shape({
      uuid: PropTypes.string,
    }),
    value: PropTypes.string,
    due_date: PropTypes.string,
  }),
  onBaixa: PropTypes.func.isRequired,
  pay_off: PropTypes.bool,
  onNew: PropTypes.func.isRequired,
};

InstallmentForm.defaultProps = {
  accounts: [],
  installment: null,
  pay_off: false,
};

export default InstallmentForm;
